import { formatProtoTimeOfDay } from 'helpers/intl-fns';

import { normalizeDayOfWeek } from './components/AddMenuFlyout.helpers';
import messages from './SkuMenuPage.messages';

const PROTO_DAY_TO_EN = {
  SUNDAY: 'Su',
  MONDAY: 'Mo',
  TUESDAY: 'Tu',
  WEDNESDAY: 'We',
  THURSDAY: 'Th',
  FRIDAY: 'Fr',
  SATURDAY: 'Sa',
};

export const formatDayOfWeek = (dayOfWeek, offsetDaysOfWeek) => {
  const normalizedDay = normalizeDayOfWeek(dayOfWeek);
  const enAbbrev = PROTO_DAY_TO_EN[normalizedDay];
  if (!enAbbrev) {
    return dayOfWeek || '';
  }

  const match = offsetDaysOfWeek?.find(d => d.en === enAbbrev);
  return match?.long || dayOfWeek;
};

const PROTO_DAY_ORDER = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const timeKey = time => `${time?.hours || 0}:${time?.minutes || 0}`;

const buildDayLabel = (dayKeys, offsetDaysOfWeek, intl) => {
  const validKeys = dayKeys.filter(day => PROTO_DAY_TO_EN[day]);
  if (validKeys.length === 0) {
    return '';
  }

  if (validKeys.length === 7) {
    return intl.formatMessage(messages.allDays);
  }

  if (validKeys.length === 1) {
    return formatDayOfWeek(validKeys[0], offsetDaysOfWeek);
  }

  const dayPositions = validKeys.map(day => {
    const enAbbrev = PROTO_DAY_TO_EN[day];
    return offsetDaysOfWeek.findIndex(d => d.en === enAbbrev);
  });
  dayPositions.sort((a, b) => a - b);

  const isLinearConsecutive = dayPositions.every((pos, i) => i === 0 || pos === dayPositions[i - 1] + 1);

  // Wrap-around consecutive: the gap from last→first (wrapping) is exactly 1,
  // and there is exactly one internal gap (the break point)
  let internalGaps = 0;
  for (let i = 1; i < dayPositions.length; i += 1) {
    if (dayPositions[i] !== dayPositions[i - 1] + 1) {
      internalGaps += 1;
    }
  }
  const wrapGap = dayPositions[0] + 7 - dayPositions[dayPositions.length - 1];
  const isWrapConsecutive = !isLinearConsecutive && internalGaps === 1 && wrapGap === 1;

  if (isLinearConsecutive) {
    const firstDay = offsetDaysOfWeek[dayPositions[0]];
    const lastDay = offsetDaysOfWeek[dayPositions[dayPositions.length - 1]];
    return `${firstDay.short}-${lastDay.short}`;
  }

  if (isWrapConsecutive) {
    // Find the break point: the one internal gap
    let breakIdx = 0;
    for (let i = 1; i < dayPositions.length; i += 1) {
      if (dayPositions[i] !== dayPositions[i - 1] + 1) {
        breakIdx = i;
        break;
      }
    }
    const rangeStart = offsetDaysOfWeek[dayPositions[breakIdx]];
    const rangeEnd = offsetDaysOfWeek[dayPositions[breakIdx - 1]];
    return `${rangeStart.short}-${rangeEnd.short}`;
  }

  return dayPositions.map(pos => offsetDaysOfWeek[pos].short).join(', ');
};

export const formatAllMenuHours = (menu, intl, offsetDaysOfWeek) => {
  if (!menu) {
    return null;
  }

  const { businessHours } = menu;
  if (!Array.isArray(businessHours) || businessHours.length === 0) {
    return null;
  }

  const validEntries = businessHours.filter(
    entry => entry?.enabled !== false && Boolean(entry?.startTime) && Boolean(entry?.endTime)
  );
  if (validEntries.length === 0) {
    return null;
  }

  const groups = {};
  const groupOrder = [];
  validEntries.forEach(entry => {
    const key = `${timeKey(entry.startTime)}-${timeKey(entry.endTime)}`;
    if (!groups[key]) {
      groups[key] = { days: [], startTime: entry.startTime, endTime: entry.endTime };
      groupOrder.push(key);
    }
    const normalizedDay = normalizeDayOfWeek(entry.dayOfWeek);
    if (normalizedDay) {
      groups[key].days.push(normalizedDay);
    }
  });

  // Sort days within each group by their position in the week
  groupOrder.forEach(key => {
    groups[key].days.sort((a, b) => PROTO_DAY_ORDER.indexOf(a) - PROTO_DAY_ORDER.indexOf(b));
  });

  // Sort groups by the earliest day position in the locale week
  const getEarliestPosition = group => {
    const positions = group.days.map(day => {
      const enAbbrev = PROTO_DAY_TO_EN[day];
      return offsetDaysOfWeek.findIndex(d => d.en === enAbbrev);
    });
    return Math.min(...positions);
  };
  groupOrder.sort((a, b) => getEarliestPosition(groups[a]) - getEarliestPosition(groups[b]));

  return groupOrder
    .map(key => {
      const { days, startTime, endTime } = groups[key];
      const dayLabel = buildDayLabel(days, offsetDaysOfWeek, intl);
      const start = formatProtoTimeOfDay(intl, startTime);
      const end = formatProtoTimeOfDay(intl, endTime);
      if (!start || !end) {
        return null;
      }
      return `${dayLabel} ${start} - ${end}`;
    })
    .filter(Boolean)
    .join(', ');
};

export const formatMenuHours = (menu, intl, offsetDaysOfWeek) => {
  if (!menu) {
    return null;
  }

  const { businessHours } = menu;
  if (!Array.isArray(businessHours) || businessHours.length === 0) {
    return null;
  }

  const firstRange = businessHours.find(entry => {
    const startTime = entry?.startTime;
    const endTime = entry?.endTime;
    return entry?.enabled !== false && Boolean(startTime) && Boolean(endTime);
  });
  if (!firstRange) {
    return null;
  }

  const { startTime, endTime, dayOfWeek } = firstRange;
  const start = formatProtoTimeOfDay(intl, startTime);
  const end = formatProtoTimeOfDay(intl, endTime);
  const dayLabel = formatDayOfWeek(dayOfWeek, offsetDaysOfWeek);

  if (!start || !end) {
    return null;
  }

  return `${dayLabel ? `${dayLabel} ` : ''}${start} - ${end}`;
};
